import Constants from 'expo-constants';

export interface ItemSpecificResponse {
  product_id: string;
  status: 'specific' | 'unavailable';
  reason: string;
  valuation: string | null;
  currency: 'USD' | null;
  identity_attempt_id: number | null;
  condition_generation: number | null;
  provenance: {
    identity_attempt_id: number;
    identity_version: string;
    pricing_identity_key: string;
    representative_instrument_id: number;
    valuation_id: number;
    as_of_date: string;
    method_version: 'v1';
    approval_id: string;
    rule_version: string;
    basis_condition: 'Used';
    condition: string;
    condition_source: string;
    condition_generation: number;
    condition_attestation: Record<string, unknown>;
    condition_policy: Record<string, unknown>;
    expires_at: string;
  } | null;
}

export const CONDITION_VALUES = ['New', 'Like New', 'Excellent', 'Good', 'Fair', 'Poor'] as const;
export type ConfirmableCondition = (typeof CONDITION_VALUES)[number];

async function request(path: string, token: string, body?: unknown) {
  const config = Constants.expoConfig?.extra;
  if (!config?.EXPO_PUBLIC_BACKEND_API_URL || !config?.EXPO_PUBLIC_BACKEND_API_SECRET) {
    throw new Error('Backend API not configured');
  }
  const response = await fetch(`${config.EXPO_PUBLIC_BACKEND_API_URL}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store',
      'X-API-Secret': config.EXPO_PUBLIC_BACKEND_API_SECRET,
      'X-User-Token': token,
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new Error('Item valuation request failed');
  return response.json();
}

export function getItemSpecificValuation(id: string, token: string): Promise<ItemSpecificResponse> {
  return request(`/items/${encodeURIComponent(id)}/specific-valuation`, token);
}

export function confirmItemCondition(
  id: string,
  token: string,
  body: {
    condition: ConfirmableCondition;
    identity_attempt_id: number;
    condition_generation: number;
    request_key: string;
  }
): Promise<{ reason: string; attestation_id: number | null }> {
  return request(`/items/${encodeURIComponent(id)}/condition-confirmation`, token, body);
}

/** A server contract may supply a value only for this exact currently rendered item. */
export function currentSpecificValue(
  response: ItemSpecificResponse | null,
  item: { id: string; condition: string | null; identity_status?: string | null },
  now = Date.now()
): string | null {
  const p = response?.provenance;
  if (
    !response ||
    response.product_id !== item.id ||
    response.status !== 'specific' ||
    response.reason !== 'approved_specific' ||
    response.currency !== 'USD' ||
    !p ||
    item.identity_status !== 'resolved' ||
    p.identity_version !== 'scan-v1' ||
    p.identity_attempt_id !== response.identity_attempt_id ||
    p.condition_generation !== response.condition_generation ||
    p.condition !== item.condition ||
    p.condition_source !== 'user_confirmed' ||
    p.basis_condition !== 'Used' ||
    p.method_version !== 'v1' ||
    !p.approval_id ||
    !p.rule_version ||
    !p.pricing_identity_key ||
    !Number.isFinite(Date.parse(p.expires_at)) ||
    Date.parse(p.expires_at) <= now ||
    !response.valuation ||
    !/^\d+(\.\d{1,2})?$/.test(response.valuation)
  )
    return null;
  return response.valuation;
}
